import React from 'react';
import { NavLink, Link } from 'react-router-dom';
import { recordV2FunnelStep, type WorkspacePage } from '../../utils/v2Rum';

type NavItem = {
  key: string;
  label: string;
  icon: string;
  enabled: boolean;
  path?: string;
  end?: boolean;
  title?: string;
};

const NAV_ITEMS: NavItem[] = [
  { key: 'overview', label: 'Overview', icon: '⊞', enabled: true, path: '', end: true },
  { key: 'insights', label: 'Insights', icon: '✦', enabled: true, path: 'insights' },
  { key: 'timeline', label: 'Roadmap', icon: '◈', enabled: true, path: 'timeline' },
  { key: 'people', label: 'Roster', icon: '◎', enabled: true, path: 'people' },
  { key: 'graph', label: 'Knowledge Graph', icon: '◇', enabled: true, path: 'graph', title: 'Analysis data feeds Insights.' },
  { key: 'documents', label: 'Documents', icon: '▢', enabled: true, path: 'documents' },
  { key: 'changelog', label: 'Change Log', icon: '◷', enabled: true, path: 'changelog' },
];

type SelaProjectSidebarProps = {
  projectId: string;
  currentPage: WorkspacePage;
};

const NAV_KEY_TO_PAGE: Record<string, WorkspacePage> = {
  overview: 'overview',
  insights: 'insights',
  timeline: 'roadmap',
  people: 'roster',
  graph: 'graph',
  documents: 'documents',
  changelog: 'changelog',
};

export function SelaProjectSidebar({ projectId, currentPage }: SelaProjectSidebarProps) {
  const base = `/project/${projectId}`;

  return (
    <aside className="flex w-[220px] shrink-0 flex-col border-r border-[#e7e9ee] bg-white">
      <div className="flex min-h-16 flex-wrap items-center gap-x-2 gap-y-1 border-b border-[#e7e9ee] px-4 py-3">
        <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" aria-hidden />
        <Link
          to="/"
          className="rounded text-lg font-bold text-[#1e4474] hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-[#3251b7] focus:ring-offset-2"
        >
          Sela
        </Link>
        <span className="rounded-full bg-[#eff2fe] px-2 py-0.5 text-[9px] font-medium leading-none text-[#3251b7]">
          predevelopment
        </span>
      </div>

      <nav aria-label="Project" className="flex flex-col gap-1 px-3 py-4">
        {NAV_ITEMS.map((item) => {
          if (item.enabled) {
            const to = item.path ? `${base}/${item.path}` : base;
            return (
              <NavLink
                key={item.key}
                to={to}
                end={item.end}
                onClick={() => {
                  const toPage = NAV_KEY_TO_PAGE[item.key] || 'overview';
                  if (toPage !== currentPage) {
                    recordV2FunnelStep('workspace_nav', 'click', {
                      from_page: currentPage,
                      to_page: toPage,
                    }, projectId);
                  }
                }}
                className={({ isActive }) =>
                  [
                    'flex h-9 items-center gap-2 rounded-md px-2.5 text-sm transition-colors',
                    isActive
                      ? 'bg-[#eff2fe] font-semibold text-[#3251b7]'
                      : 'font-normal text-[#383e4d] hover:bg-slate-50',
                  ].join(' ')
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`w-4 text-center ${isActive ? 'text-[#3251b7]' : 'text-[#858a9a]'}`}
                      aria-hidden
                    >
                      {item.icon}
                    </span>
                    {item.label}
                  </>
                )}
              </NavLink>
            );
          }

          return (
            <div
              key={item.key}
              className="flex h-9 cursor-not-allowed items-center gap-2 rounded-md px-2.5 text-sm text-[#383e4d]/50"
              aria-disabled="true"
              title={item.title || 'Coming soon'}
            >
              <span className="w-4 text-center text-[#858a9a]/60" aria-hidden>
                {item.icon}
              </span>
              {item.label}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
